import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { AttentionRow } from "../components/mission-bits";
import { Eyebrow, Empty, Skeleton } from "../components/ui/primitives";
import { get } from "../lib/api";
import { fmtInt, fmtMoney, fmtTokens, relTime } from "../lib/format";
import { EVENT_LABEL } from "../lib/labels";
import type { OverviewData } from "../lib/types";
import { useCompany } from "../lib/company";

function Kpi({ label, value, sub, tone, to }: { label: string; value: string | number; sub: string; tone?: "er" | "wn" | "gd"; to?: string }) {
  const body = (
    <div className="flex h-full flex-col gap-1.5 rounded-card border border-ln bg-sf px-4 py-3.5">
      <div className="flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-[0.12em] text-mu">{tone && <span className="h-2 w-2 rounded-full" style={{ background: `var(--${tone})` }} />}{label}</div>
      <div className="font-display text-[28px] font-medium leading-none tracking-[-0.02em]">{value}</div>
      <div className="text-[13px] text-mu">{sub}</div>
    </div>
  );
  return to ? <Link to={to} className="block hover:opacity-90">{body}</Link> : body;
}

export function OverviewPage() {
  const { companyId, qs, current } = useCompany();
  const q = useQuery({ queryKey: ["overview", companyId], queryFn: () => get<OverviewData>(`/overview${qs()}`), refetchInterval: 8_000 });
  const d = q.data;
  const k = d?.kpis;
  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 pb-24 pt-5 md:px-8 md:pb-12 md:pt-8">
      <Eyebrow>Centro de operaciones{current ? ` · ${current.name}` : ""}</Eyebrow>
      <h1 className="m-0 mt-2 font-display text-[32px] font-medium leading-[1.1] tracking-[-0.025em]">Overview</h1>

      {/* La zona más importante va primero, también en móvil */}
      <div className="mt-6 grid items-start gap-8 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <section aria-labelledby="attn">
          <div className="mb-3 flex items-baseline justify-between"><h2 id="attn" className="m-0 font-display text-[20px] font-medium tracking-[-0.02em]">Requiere tu atención</h2><span className="font-mono text-[12px] text-mu">{d?.attention.length ?? "…"}</span></div>
          <div className="overflow-hidden rounded-card border border-ln bg-sf">
            {!d && <div className="space-y-2 p-4"><Skeleton className="h-12" /><Skeleton className="h-12" /></div>}
            {d?.attention.map((c) => <AttentionRow key={c.id} c={c} />)}
            {d && !d.attention.length && <Empty>Nada pendiente. La fábrica trabaja sola; te avisaremos cuando necesite una decisión.</Empty>}
          </div>
        </section>

        <section>
          <h2 className="m-0 mb-3 font-display text-[20px] font-medium tracking-[-0.02em]">Actividad reciente</h2>
          <div className="flex flex-col">
            {d?.recentEvents.map((e) => (
              <div key={e.id} className="grid grid-cols-[72px_1fr] gap-2.5 border-t border-ln py-2.5 text-[14px] leading-snug">
                <span className="pt-0.5 font-mono text-[12px] text-mu">{relTime(e.at)}</span>
                <span>{e.missionId ? <Link to={`/missions/${e.missionId}`} className="hover:underline"><span className="font-mono text-[12px] text-mu">{e.missionKey} · </span>{EVENT_LABEL[e.type] ?? e.type}</Link> : EVENT_LABEL[e.type] ?? e.type}</span>
              </div>
            ))}
            {d && !d.recentEvents.length && <Empty>Sin actividad todavía.</Empty>}
          </div>
        </section>
      </div>

      <div className="mt-8 grid gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]">
        {!k && Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-[104px]" />)}
        {k && (
          <>
            <Kpi label="Decisiones pendientes" value={k.pendingDecisions} sub="planes y entregas por aprobar" tone={k.pendingDecisions ? "wn" : undefined} to="/missions?scope=attention" />
            <Kpi label="Bloqueos y errores" value={k.blockedOrFailed} sub="necesitan acción" tone={k.blockedOrFailed ? "er" : undefined} to="/missions?scope=attention" />
            <Kpi label="En desarrollo" value={k.inProgressMissions} sub="planificando, programando o en CI" />
            <Kpi label="Pendientes" value={k.pendingMissions} sub="en cola de arranque" />
            <Kpi label="Terminadas" value={k.doneMissions} sub="misiones cerradas" />
            <Kpi label="PR abiertos" value={k.openPullRequests} sub="por revisar o fusionar" />
            <Kpi label="Agentes activos" value={k.activeAgents} sub="ejecutando ahora" tone={k.activeAgents ? "gd" : undefined} to="/agents" />
            <Kpi label="Proyectos" value={k.activeProjects} sub={`${k.authorizedRepositories} repositorios autorizados`} to="/projects" />
            <Kpi label="Consumo del mes" value={`${fmtTokens(k.monthTokens)} tok`} sub={k.monthEstimatedCost ? `≈ ${fmtMoney(k.monthEstimatedCost, 0)} (estimado)` : "tokens medidos · sin precios configurados"} to="/costs" />
            <Kpi label="Presupuesto restante" value={k.budgetRemaining ? (k.budgetRemaining.unit === "tokens" ? `${fmtTokens(k.budgetRemaining.value)} tok` : fmtMoney({ amount: k.budgetRemaining.value, currency: k.budgetRemaining.currency ?? "EUR" }, 0)) : "—"} sub={k.budgetRemaining ? "del presupuesto global mensual" : "sin presupuesto global definido"} to="/costs" />
          </>
        )}
      </div>
      {k && <p className="mt-4 text-[12px] text-mu">Las cifras de consumo son tokens medidos por el proveedor; un importe aparece solo si un admin configuró precios, y nunca equivale a la factura. Total mes: {fmtInt(k.monthTokens)} tokens.</p>}
    </div>
  );
}
